// src/pages/PublicPath.tsx
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import Navbar from '@/components/Navbar';
import PathGraph from '@/components/PathGraph';

export default function PublicPath() {
  const { id } = useParams<{ id: string }>();

  // 1) Load path metadata
  const { data: path, isLoading: loadingPath } = useQuery({
    queryKey: ['public-path', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('paths')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // 2) Load path nodes
  const { data: nodes = [], isLoading: loadingNodes } = useQuery({
    queryKey: ['public-path-nodes', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('path_nodes')
        .select('*')
        .eq('path_id', id)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!path?.is_public, // Only load nodes if path exists and is public
  });

  // Memoize collection IDs
  const ids = useMemo(
    () => Array.from(new Set(nodes.map(n => n.collection_id).filter(Boolean))),
    [nodes]
  );

  // 3) Load collections
  const { data: collections = [] } = useQuery({
    queryKey: ['public-path-collections', id, ids.join(',')],
    queryFn: async () => {
      if (!ids.length) return [];
      const { data, error } = await supabase
        .from('collections')
        .select('id, name, public_id')
        .in('id', ids);
      if (error) throw error;
      return data;
    },
    enabled: ids.length > 0 && !!path?.is_public,
  });

  // 4) Load collection videos only for those collections
  const publicIds = useMemo(() => collections.map(c => c.id), [collections]);

  const { data: collectionVideos = [] } = useQuery({
    queryKey: ['public-path-videos', id, publicIds.join(',')],
    queryFn: async () => {
      if (!publicIds.length) return [];
      const { data, error } = await supabase
        .from('collection_videos')
        .select('*')
        .in('collection_id', publicIds);
      if (error) throw error;
      return data;
    },
    enabled: publicIds.length > 0 && !!path?.is_public,
  });

  if (loadingPath || loadingNodes) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-1/3 bg-muted rounded"></div>
            <div className="h-[60vh] bg-muted rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!path || !path.is_public) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-4">Path not found</h1>
          <p className="text-muted-foreground">
            This learning path doesn't exist or is no longer public.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">{path.title}</h1>
          {path.description && <p className="text-muted-foreground">{path.description}</p>}
        </div>
        <div className="h-[calc(100vh-250px)]">
          <PathGraph
            nodes={nodes}
            collections={collections}
            collectionVideos={collectionVideos}
            selectedPathId={id!}
            isReadOnly
          />
        </div>
      </main>
    </div>
  );
}